import React, { useState } from 'react';
import { useStaffRole } from '../hooks/useStaffRole';

// AdminPwaInstallPrompt — a small dismissible nudge telling staff how to add
// DoorlyDesk to their iPhone home screen. iOS Safari has no programmatic
// install API (no beforeinstallprompt), so the only path is Share → Add to
// Home Screen — hence the instructional copy rather than a button.
//
// Shown only when ALL are true:
//   • iOS device (iPhone/iPad/iPod)            — Android/desktop have their own install UX
//   • not already running as an installed app  — window.navigator.standalone
//   • not previously dismissed                 — localStorage flag
//   • current user is a Doorly admin           — Jesse/Hannah; LO/RE-desk skip it
//
// The route-aware PWA identity swap lives in App.js (PwaManifestSwitcher); by
// the time this renders the user is on an /admin route, so Add to Home Screen
// already picks up the DoorlyDesk icon/title.
const DISMISS_KEY = 'doorlydesk_pwa_dismissed';

export default function AdminPwaInstallPrompt() {
  const { isAdmin } = useStaffRole();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent || '');
  // Safari standalone flag is true once launched from the home screen.
  const isStandalone = window.navigator.standalone === true;

  if (!isAdmin || !isIOS || isStandalone || dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode — just hide */ }
    setDismissed(true);
  };

  return (
    <div
      role="note"
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        backgroundColor: '#1f2937', color: '#ffffff',
        fontSize: 11, lineHeight: 1.4,
        padding: '8px 12px', margin: '10px 12px 0', borderRadius: 8,
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 14 }}>📱</span>
      <span style={{ flex: 1 }}>
        <strong style={{ fontWeight: 700 }}>Add DoorlyDesk to your home screen.</strong>{' '}
        Tap <strong>Share</strong> → <strong>Add to Home Screen</strong> for the best experience.
      </span>
      <button
        type="button"
        onClick={dismiss}
        style={{
          flexShrink: 0, background: 'transparent', border: '1px solid rgba(255,255,255,0.25)',
          color: '#ffffff', fontSize: 11, fontWeight: 600, padding: '4px 10px',
          borderRadius: 6, cursor: 'pointer',
        }}
      >
        Dismiss
      </button>
    </div>
  );
}
